import { defineStore } from "pinia";
import axios from "axios";
import type {
  CompanyInformation,
  UserMeResponse,
  UserProfile,
} from "@/core/api/data-contracts";
import { getApplicationInsightsInstance } from "../../services/application-insights/application-insight";
import {
  identifyPostHogUser,
  isPostHogUserIdentified,
  resetPostHog,
  setPostHogCompany,
} from "@/services/posthog";

export type UserState =
  | {
      isAuthenticated: true;
      roles: string[];
      profile: UserProfile;
      companies: CompanyInformation[];
      activeCompany: CompanyInformation;
    }
  | {
      isAuthenticated: false;
      roles: string[];
      profile: null;
      companies: CompanyInformation[];
      activeCompany: null;
    };

export type UserRole =
  | "se.eaztimate.ifsplanering"
  | "se.eaztimate.ifsplanering.user"
  | "se.eaztimate.ifsplanering.planner";

export type AuthenticationState = "missing" | "initialized" | "unknown";

export type StoreState = {
  authenticationState: AuthenticationState;
  user: UserState;
};

const roles = {
  user: "se.eaztimate.ifsplanering.user",
  planner: "se.eaztimate.ifsplanering.planner",
} as const;

type MainRoles = "foreman" | "worker" | "anonymous";

const anonymousUser: UserState = {
  isAuthenticated: false,
  profile: null,
  companies: [],
  roles: [],
  activeCompany: null,
} as const;

export const useAuthenticatedUserStore = defineStore("authenticatedUser", {
  state: (): StoreState => ({
    authenticationState: "missing",
    user: anonymousUser,
  }),
  getters: {
    userCanView: (state: StoreState): boolean =>
      isInRole(state, roles.user) || isInRole(state, roles.planner),
    userCanEdit: (state: StoreState): boolean => isInRole(state, roles.planner),
    mainRole(): MainRoles {
      if (this.userCanEdit) {
        return "foreman";
      } else if (this.userCanView) {
        return "worker";
      } else {
        return "anonymous";
      }
    },
    userName(): string | null {
      if (this.user.isAuthenticated) {
        return this.user.profile.name;
      } else {
        return null;
      }
    },
  },
  actions: {
    async userLogin(path: string) {
      // We're leaving the site now, so it's better to flush AI before.
      getApplicationInsightsInstance().flush();
      location.href = `/api/user/login?redirect=${path}`;
    },
    async userLogout() {
      resetPostHog();
      // We're leaving the site now, so it's better to flush AI before.
      getApplicationInsightsInstance().flush();
      location.href = "/api/user/logout";
    },
    async updateUser(
      userData: UserState,
      options: { source: "user-init" | "user-update" },
    ) {
      this.user = userData;
      this.authenticationState = "initialized";

      if (userData.isAuthenticated) {
        setPostHogCompany(userData.activeCompany);
      } else if (isPostHogUserIdentified()) {
        // Session lost (e.g. after a 401): stop attributing events to the previous user
        resetPostHog();
      }

      if (userData.isAuthenticated && options.source === "user-init") {
        // distinct_id is the stable userId, same as HubbVue
        identifyPostHogUser(userData.profile.userId.toString(), {
          email: userData.profile.email,
          role: this.mainRole,
          company_id: userData.activeCompany.id,
          company_name: userData.activeCompany.name,
        });
      }
    },
    /** To be able to execute http requests we'll need to make sure that the user is initialized */
    async requireUser(): Promise<UserState> {
      await this.userInit();
      return this.user;
    },
    /** Forces reevaluation of the user since we can not be sure if the user is logged in anymore */
    revaluateAuthenticatedUser() {
      this.authenticationState = "unknown";
    },
    async userInit() {
      // NOTE: since there are multiple async calls this call might be racy and we might need to add something like
      // https://github.com/DirtyHairy/async-mutex
      const state = this.authenticationState;
      if (state === "initialized") {
        return;
      }

      try {
        const user = await getCurrentUser();

        const updatedUserState = toUserState(user, null);
        await this.updateUser(updatedUserState, {
          source: state === "missing" ? "user-init" : "user-update",
        });
      } catch {
        await this.userLogout();
      }
    },
  },
});

function toUserState(
  user: UserMeResponse,
  preferredCompanyId: string | null,
): UserState {
  if (!user.isAuthenticated || !user.profile) {
    return anonymousUser;
  }

  const preferredCompany = preferredCompanyId
    ? user.companies.find((company) => company.id === preferredCompanyId)
    : null;

  return {
    isAuthenticated: true,
    profile: user.profile,
    roles: user.roles,
    companies: user.companies,
    activeCompany: preferredCompany ?? user.profile.mainCompany,
  };
}

function isInRole(state: StoreState, role: UserRole) {
  return state.user.roles.includes(role);
}

async function getCurrentUser() {
  const user = await axios.get<UserMeResponse>("/api/user/me");

  return user.data;
}
